<template>
  <div class="upload-component">
    <!-- 文件选择区域 -->
    <div class="upload-area" @click.stop="triggerFileInput" @drop.prevent="handleDrop" @dragover.prevent>
      <input
        type="file"
        ref="fileInput"
        :multiple="multiple"
        :accept="accept"
        @change="handleFileChange"
        class="hidden"
      />
      <div class="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
        <i class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
        <p class="text-sm text-gray-600">点击或拖拽文件到此处上传</p>
        <p class="text-xs text-gray-400 mt-1" v-if="accept">
          支持的文件类型: {{ accept }}
        </p>
        <p class="text-xs text-gray-400" v-if="maxSize">
          文件大小限制: {{ formatSize(maxSize) }}
        </p>
      </div>
    </div>

    <!-- 文件列表 -->
    <div class="file-list mt-4" v-if="fileList.length > 0">
      <div
        v-for="(file, index) in fileList"
        :key="index"
        class="file-item flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-2"
      >
        <div class="flex items-center space-x-2">
          <!-- 预览图片 -->
          <template v-if="file.preview">
            <img :src="file.preview" class="w-10 h-10 object-cover rounded cursor-pointer" @click="showImagePreview(file)" />
          </template>
          <template v-else>
            <i class="fas" :class="{
              'fa-file-image text-blue-500': file.type?.startsWith('image/'),
              'fa-file-pdf text-red-500': file.type === 'application/pdf',
              'fa-file-word text-blue-700': file.type?.includes('word'),
              'fa-file-excel text-green-600': file.type?.includes('excel') || file.type?.includes('spreadsheet'),
              'fa-file text-gray-400': !file.type?.startsWith('image/') && !file.type?.includes('pdf') && !file.type?.includes('word') && !file.type?.includes('excel')
            }"></i>
          </template>
          <span class="text-sm">{{ file.name }}</span>
          <span class="text-xs text-gray-500" v-if="file.size">({{ formatSize(file.size) }})</span>
        </div>
        
        <div class="flex items-center space-x-4">
          <!-- 上传进度 -->
          <template v-if="file.status === 'uploading'">
            <i class="fas fa-spinner fa-spin text-blue-500"></i>
           
            <!-- <div class="w-24 bg-gray-200 rounded-full h-2">
              <div
                class="bg-blue-500 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${file.progress}%` }"
              ></div>
            </div>
            <span class="text-xs text-gray-500">{{ file.progress }}%</span> -->
          </template>

          <!-- 状态图标 -->
          <template v-if="file.status === 'success'">
            <i class="fas fa-check-circle text-green-500"></i>
          </template>
          <template v-if="file.status === 'error'">
            <i class="fas fa-times-circle text-red-500" :title="file.error"></i>
          </template>

          <!-- 查看大图按钮 (仅对图片显示) -->
          <button
            v-if="file.status === 'success' && file.type?.startsWith('image/')"
            @click="showImagePreview(file)"
            class="text-gray-400 hover:text-blue-500 transition-colors"
            title="查看大图"
          >
            <i class="fas fa-search-plus"></i>
          </button>

          <!-- 下载按钮 -->
          <button
            v-if="file.status === 'success' && file.serverData?.url"
            @click="downloadFile(file)"
            class="text-gray-400 hover:text-green-500 transition-colors"
            title="下载文件"
          >
            <i class="fas fa-download"></i>
          </button>

          <!-- 删除按钮 -->
          <button
            @click="removeFile(index)"
            class="text-gray-400 hover:text-red-500 transition-colors"
            title="删除文件"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 图片预览模态框 -->
    <Teleport to="body">
      <div v-if="previewVisible" class="image-preview-modal" @click="closeImagePreview">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">{{ previewFile?.name }}</h3>
            <button @click="closeImagePreview" class="close-button">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body-container">
            <div class="modal-body" ref="modalBody">
              <div class="image-container" ref="imageContainer" @wheel="handleWheel" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp" @mouseleave="handleMouseUp">
                <img 
                  :src="previewFile?.preview || previewFile?.serverData?.url" 
                  class="preview-image" 
                  ref="previewImage"
                  :style="imageStyle"
                  @dragstart.prevent
                />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="footer-left" v-if="imageFiles.length > 1">
              <div class="thumbnail-list">
                <div 
                  v-for="(file, index) in imageFiles" 
                  :key="index"
                  class="thumbnail-item"
                  :class="{ 'active': file === previewFile }"
                  @click="switchPreviewImage(file)"
                >
                  <img :src="file.preview || file.serverData?.url" :alt="file.name" />
                </div>
              </div>
              <div class="image-counter">
                {{ currentImageIndex + 1 }} / {{ imageFiles.length }}
              </div>
            </div>
            <div class="flex gap-2 footer-right">
              <button @click="downloadFile(previewFile)" class="download-button" v-if="previewFile?.serverData?.url">
                <i class="fas fa-download mr-1"></i> 下载
              </button>
              <button @click="closeImagePreview" class="close-modal-button">
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed, defineExpose, onMounted, onUnmounted } from 'vue'
import { toast } from '@/utils/toast.js'

const props = defineProps({
  // 是否支持多文件上传
  multiple: {
    type: Boolean,
    default: false
  },
  // 接受的文件类型
  accept: {
    type: String,
    default: '.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx'
  },
  // 文件大小限制（字节）
  maxSize: {
    type: Number,
    default: 0
  },
  // 自动上传
  autoUpload: {
    type: Boolean,
    default: true
  },
  // 上传地址
  action: {
    type: String,
    required: true
  },
  // 已上传的文件列表
  attachmentUrl: {
    type: String,
    default: ''
  },
  // v-model绑定的值
  modelValue: {
    type: [String, Array],
    default: () => []
  }
})

const emit = defineEmits([
  'file-added',
  'file-removed',
  'upload-success',
  'upload-error',
  'upload-progress',
  'upload-list',
  'update:modelValue'
])

const fileInput = ref(null)
const fileList = ref([])

// 监听modelValue变化，初始化文件列表
watch(() => props.modelValue, (newValue) => {
  if (newValue && newValue !== '') {
    if (props.multiple && Array.isArray(newValue)) {
      // 多文件模式
      const existingUrls = fileList.value.map(f => f.url)
      newValue.forEach(url => {
        if (url && !existingUrls.includes(url)) {
          fileList.value.push({
            name: url.split('/').pop() || 'file',
            url: url,
            status: 'success',
            size: 0
          })
        }
      })
    } else if (!props.multiple && typeof newValue === 'string') {
      // 单文件模式
      const existingFile = fileList.value.find(f => f.url === newValue)
      if (!existingFile && newValue) {
        fileList.value = [{
          name: newValue.split('/').pop() || 'file',
          url: newValue,
          status: 'success',
          size: 0
        }]
      }
    }
  } else {
    // 如果modelValue为空，清空文件列表
    fileList.value = []
  }
}, { immediate: true })

// 图片预览相关
const previewVisible = ref(false)
const previewFile = ref(null)
const modalBody = ref(null)
const imageContainer = ref(null)
const previewImage = ref(null)

// 计算所有图片文件
const imageFiles = computed(() => {
  return fileList.value.filter(file => 
    file.type?.startsWith('image/') || 
    (file.serverData?.url && isImageUrl(file.serverData.url))
  )
})

// 当前预览图片索引
const currentImageIndex = computed(() => {
  return imageFiles.value.findIndex(file => file === previewFile.value)
})

// 切换预览图片
const switchPreviewImage = (file) => {
  if (file === previewFile.value) return
  previewFile.value = file
  resetImageTransform()
}

// 图片缩放和拖动相关
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const lastTranslateX = ref(0)
const lastTranslateY = ref(0)

// 计算图片样式
const imageStyle = computed(() => ({
  transform: `scale(${scale.value}) translate(${translateX.value}px, ${translateY.value}px)`,
  cursor: isDragging.value ? 'grabbing' : 'grab',
  transition: isDragging.value ? 'none' : 'transform 0.2s ease'
}))

// 显示图片预览
const showImagePreview = (file) => {
  if (file.type?.startsWith('image/') || (file.serverData?.url && isImageUrl(file.serverData.url))) {
    previewFile.value = file
    previewVisible.value = true
    // 重置缩放和位置
    resetImageTransform()
  }
}

// 关闭图片预览
const closeImagePreview = () => {
  previewVisible.value = false
  resetImageTransform()
}

// 重置图片变换
const resetImageTransform = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
  isDragging.value = false
  lastTranslateX.value = 0
  lastTranslateY.value = 0
}

// 处理鼠标滚轮缩放
const handleWheel = (event) => {
  event.preventDefault()
  const delta = event.deltaY > 0 ? -0.1 : 0.1
  const newScale = Math.max(0.1, Math.min(5, scale.value + delta))
  scale.value = newScale
}

// 处理鼠标按下开始拖动
const handleMouseDown = (event) => {
  if (scale.value > 1) {
    isDragging.value = true
    dragStartX.value = event.clientX
    dragStartY.value = event.clientY
    lastTranslateX.value = translateX.value
    lastTranslateY.value = translateY.value
  }
}

// 处理鼠标移动拖动
const handleMouseMove = (event) => {
  if (isDragging.value && scale.value > 1) {
    const deltaX = event.clientX - dragStartX.value
    const deltaY = event.clientY - dragStartY.value
    translateX.value = lastTranslateX.value + deltaX
    translateY.value = lastTranslateY.value + deltaY
  }
}

// 处理鼠标抬起结束拖动
const handleMouseUp = () => {
  isDragging.value = false
}

// 判断URL是否为图片
const isImageUrl = (url) => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
  return imageExtensions.some(ext => url.toLowerCase().endsWith(ext))
}

// 下载文件
const downloadFile = (file) => {
  if (!file || !file.serverData?.url) {
    toast.error('下载链接不存在')
    return
  }

  try {
    const link = document.createElement('a')
    link.href = file.serverData.url
    link.download = file.name || 'download'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('下载文件失败:', error)
    toast.error('下载文件失败')
  }
}

// 初始化已上传的文件列表
const initAttachmentList = () => {
  if (!props.attachmentUrl) return
  try {
    // 如果fileList已经有内容，不进行初始化
    if (fileList.value.length > 0) {
      console.log('fileList已有内容，跳过初始化')
      return
    }
    
    const attachments = JSON.parse(props.attachmentUrl)
    const files = attachments.map(attachment => ({
      name: attachment.fileName,
      status: 'success',
      preview: attachment.url,
      type: getFileTypeFromUrl(attachment.url), // 根据URL判断文件类型
      serverData: {
        url: attachment.url,
        id: attachment.id
      }
    }))
    fileList.value = files
    console.log('初始化attachmentUrl:', fileList.value)
  } catch (error) {
    console.error('解析 attachmentUrl 失败:', error)
  }
}

// 根据URL判断文件类型
const getFileTypeFromUrl = (url) => {
  if (!url) return 'application/octet-stream'
  
  const extension = url.split('.').pop()?.toLowerCase()
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  const pdfExts = ['pdf']
  const wordExts = ['doc', 'docx']
  const excelExts = ['xls', 'xlsx']
  
  if (imageExts.includes(extension)) return 'image/jpeg'
  if (pdfExts.includes(extension)) return 'application/pdf'
  if (wordExts.includes(extension)) return 'application/msword'
  if (excelExts.includes(extension)) return 'application/vnd.ms-excel'
  
  return 'application/octet-stream'
}

// 监听 attachmentUrl 变化
watch(() => props.attachmentUrl, (newVal, oldVal) => {
  // 只在组件初始化或attachmentUrl从空变为有值时初始化文件列表
  // 避免在上传过程中重新初始化列表导致清空
  if (!oldVal || oldVal === '') {
    initAttachmentList()
  }
}, { immediate: true })

// 触发文件选择
const triggerFileInput = () => {
  fileInput.value.click()
}

// 处理文件选择
// 添加控制台日志调试
const handleFileChange = (event) => {
  const files = Array.from(event.target.files)
  addFiles(files)
  event.target.value = '' // 重置input，允许选择相同文件
}

// 处理拖拽
const handleDrop = (event) => {
  const files = Array.from(event.dataTransfer.files)
  addFiles(files)
}

// // 添加文件
// const calculateFileHash = async (file) => {
//   const buffer = await file.arrayBuffer();
//   const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
//   const hashArray = Array.from(new Uint8Array(hashBuffer));
//   return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
// };

const addFiles = async (files) => {
  for (const file of files) {
    // 基础验证
    if (props.maxSize && file.size > props.maxSize) {
      emit('upload-error', file, `文件大小超过限制 ${formatSize(props.maxSize)}`)
      toast.error(file.name+`文件大小超过限制 ${formatSize(props.maxSize)}`)
      continue
    }

    // 重复文件验证
    const isDuplicate = fileList.value.some(existing => 
      existing.name === file.name && 
      existing.size === file.size
    );

    if (isDuplicate) {
      toast.error(file.name+'重复文件（基础验证：名称+大小）')
      emit('upload-error', file, '重复文件（基础验证：名称+大小）')
      continue
    }

    // // 计算文件哈希
    // const fileHash = await calculateFileHash(file);
    // const isExactDuplicate = fileList.value.some(existing => 
    //   existing.hash === fileHash
    // );

    // if (isExactDuplicate) {

    //   emit('upload-error', file, '重复文件（精确哈希验证）')
    //   continue
    // }

    const fileInfo = {
      file,
      name: file.name,
      size: file.size, 
      status: 'ready',
      progress: 0,
      preview: null,
      type: file.type
    }

    // 如果是图片，创建预览URL
    if (file.type.startsWith('image/')) {
      fileInfo.preview = URL.createObjectURL(file)
    }

    fileList.value.push(fileInfo)
    emit('file-added', fileInfo)

    if (props.autoUpload) {
      uploadFile(fileInfo)
    } 
  }
}

// 更新modelValue
const updateModelValue = () => {
  const uploadedFiles = fileList.value
    .filter(file => file.status === 'success' && file.url)
    .map(file => file.url)
  
  if (props.multiple) {
    emit('update:modelValue', uploadedFiles)
  } else {
    emit('update:modelValue', uploadedFiles[0] || '')
  }
}

// 移除文件
const removeFile = (index) => {
  const file = fileList.value[index]
  // 如果存在预览URL，释放它
  if (file.preview) {
    URL.revokeObjectURL(file.preview)
  }
  fileList.value.splice(index, 1)
  emit('file-removed', file)
  emit('upload-list', fileList.value)
  updateModelValue()
}

// 上传文件
// 添加 API 引用
import { baseApi } from '@/api/api'

// 修改后的上传方法
const uploadFile = async (fileInfo) => {
  const formData = new FormData()
  formData.append('file', fileInfo.file)

  fileInfo.status = 'uploading'
  try {
    const response = await baseApi.ImageUploader(formData, {
      onUploadProgress: progressEvent => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        fileInfo.progress = progress
        emit('upload-progress', fileInfo, progress)
      }
    })
    if(response.success){
      // 获取URL - 兼容字符串和对象形式的返回
      let fileUrl = '';
      if (typeof response.data === 'string') {
        fileUrl = response.data;
      } else if (response.data && typeof response.data === 'object') {
        fileUrl = response.data.url || response.data.path || response.data.filePath || '';
      }

      // 更新文件状态
      const updatedFile = {
        ...fileInfo,
        status: 'success',
        serverData: response.data,
        url: fileUrl
      };
      
      // 更新fileList中对应的文件
      const fileIndex = fileList.value.findIndex(f => f.file === fileInfo.file)
      if (fileIndex !== -1) {
        fileList.value[fileIndex] = updatedFile
      }
      
      console.log('文件上传成功:', updatedFile.name, 'URL:', fileUrl);
      console.log('当前文件列表:', fileList.value);
      
      // 触发上传成功事件
      emit('upload-success', updatedFile, response.data);
      
      // 触发文件列表更新事件
      emit('upload-list', fileList.value);
      
      // 更新modelValue
      updateModelValue();
    }
  } catch (error) {
    fileInfo.status = 'error'
    fileInfo.error = error.response?.data?.message || error.message
    emit('upload-error', fileInfo, error)
  }
}

// 格式化文件大小
const formatSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
}

// 处理粘贴事件（已移除，统一使用handleClipboardPaste处理）

// 统一的粘贴事件处理
const handleClipboardPaste = async (event) => {
  // 首先尝试使用传统的clipboardData API
  const clipboardData = event.clipboardData || window.clipboardData
  
  if (clipboardData && clipboardData.items) {
    const items = clipboardData.items
    let hasImageOrFile = false
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      
      // 检查是否是图片或文件类型
      if (item.type.startsWith('image/') || (item.kind === 'file' && !item.type.startsWith('text/'))) {
        hasImageOrFile = true
        event.preventDefault()
        
        const blob = item.getAsFile()
        
        if (!blob) {
          continue
        }
        
        // 检查文件数量限制
        if (!props.multiple && fileList.value.length >= 1) {
          toast.error('当前模式下只能上传一个文件')
          return
        }
        
        // 检查文件大小
        if (props.maxSize && blob.size > props.maxSize) {
          toast.error(`粘贴的文件大小超过限制 ${formatSize(props.maxSize)}`)
          return
        }
        
        // 创建文件对象
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        let fileName, extension
        
        if (item.type.startsWith('image/')) {
          extension = item.type.split('/')[1] || 'png'
          fileName = `pasted-image-${timestamp}.${extension}`
        } else {
          // 尝试从文件名获取扩展名，否则使用通用扩展名
          extension = blob.name ? blob.name.split('.').pop() : 'bin'
          fileName = `pasted-file-${timestamp}.${extension}`
        }
        
        const file = new File([blob], fileName, { type: item.type })
        
        // 添加文件到列表
        addFiles([file])
        
        toast.success(item.type.startsWith('image/') ? '图片粘贴成功！' : '文件粘贴成功！')
        return // 处理完第一个文件后立即返回，避免重复处理
      }
    }
    
    // 如果没有图片或文件，让文本正常粘贴到文本框
    if (!hasImageOrFile) {
      // 不阻止默认行为，让文本正常粘贴
      return
    }
  }
  
  // 如果传统API不可用，尝试使用现代Clipboard API作为备用
  try {
    if (navigator.clipboard && navigator.clipboard.read) {
      const clipboardItems = await navigator.clipboard.read()
      
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/') || type.startsWith('application/')) {
            event.preventDefault()
            
            const blob = await clipboardItem.getType(type)
            
            // 检查文件数量限制
            if (!props.multiple && fileList.value.length >= 1) {
              toast.error('当前模式下只能上传一个文件')
              return
            }
            
            // 检查文件大小
            if (props.maxSize && blob.size > props.maxSize) {
              toast.error(`粘贴的文件大小超过限制 ${formatSize(props.maxSize)}`)
              return
            }
            
            // 创建文件对象
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
            let fileName, extension
            
            if (type.startsWith('image/')) {
              extension = type.split('/')[1] || 'png'
              fileName = `pasted-image-${timestamp}.${extension}`
            } else {
              extension = 'bin'
              fileName = `pasted-file-${timestamp}.${extension}`
            }
            
            const file = new File([blob], fileName, { type })
            
            // 添加文件到列表
            addFiles([file])
            
            toast.success(type.startsWith('image/') ? '图片粘贴成功！' : '文件粘贴成功！')
            return // 处理完第一个文件后立即返回，避免重复处理
          }
        }
      }
    }
  } catch (error) {
    console.error('现代Clipboard API粘贴失败:', error)
    // 如果API调用失败，不阻止默认行为，让文本正常粘贴
  }
}

// 组件挂载时添加粘贴事件监听
onMounted(() => {
  document.addEventListener('paste', handleClipboardPaste)
})

// 组件卸载时移除粘贴事件监听
onUnmounted(() => {
  document.removeEventListener('paste', handleClipboardPaste)
})

// 获取当前值
const getValue = () => {
  const uploadedFiles = fileList.value
    .filter(file => file.status === 'success' && file.url)
    .map(file => file.url)
  
  if (props.multiple) {
    return uploadedFiles
  } else {
    return uploadedFiles[0] || ''
  }
}

// 设置值
const setValue = (value) => {
  if (value && value !== '') {
    if (props.multiple && Array.isArray(value)) {
      // 多文件模式
      fileList.value = value.map(url => ({
        name: url.split('/').pop() || 'file',
        url: url,
        status: 'success',
        size: 0
      }))
    } else if (!props.multiple && typeof value === 'string') {
      // 单文件模式
      fileList.value = [{
        name: value.split('/').pop() || 'file',
        url: value,
        status: 'success',
        size: 0
      }]
    }
  } else {
    fileList.value = []
  }
  updateModelValue()
}

// 暴露方法给父组件
defineExpose({
  addFiles,
  getValue,
  setValue
})
</script>

<style scoped>
.hidden {
  display: none;
}

.upload-area {
  cursor: pointer;
}

.file-item:hover {
  background-color: #f3f4f6;
}

/* 图片预览模态框样式 */
.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%; 
  height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80%;
}

.close-button {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1.25rem;
  padding: 0.25rem;
  transition: color 0.2s;
}

.close-button:hover {
  color: #ef4444;
}

.modal-body-container {
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  position: relative;
}

.modal-body {
  padding: 1rem;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  position: relative;
  background-color: #f9fafb;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  user-select: none;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  transform-origin: center;
  user-select: none;
  pointer-events: auto;
}

.modal-footer {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-right: auto;
  overflow: hidden;
  max-width: 70%;
}

.footer-right {
  margin-left: auto;
}

.thumbnail-list {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 2px;
  /* 隐藏滚动条但保留功能 */
  scrollbar-width: thin;
}

.thumbnail-item {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
  opacity: 0.7;
  flex-shrink: 0;
}

.thumbnail-item:hover {
  opacity: 1;
}

.thumbnail-item.active {
  border-color: #3b82f6;
  opacity: 1;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-counter {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

.download-button {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
}

.download-button:hover {
  background-color: #059669;
}

.close-modal-button {
  background-color: #6b7280;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.close-modal-button:hover {
  background-color: #4b5563;
}
</style>